import { useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { ApiError, api } from '@/api/client';
import { AccountContext, type Account, type Providers } from './account';
import { consumeSsoParams, markAutoLoginAttempt, navigate, skipAutoLogin, SSO_ERRORS, ssoLoginUrl } from './sso';

export const authKeys = { me: ['auth', 'me'] as const, providers: ['auth', 'providers'] as const };

/** Loads the session; without one it tries the silent SSO login once, otherwise shows the login page. */
export function AuthGate({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: authKeys.me,
    queryFn: () => api.get<{ user: Account }>('/auth/me').then((r) => r.user),
    retry: false,
    staleTime: 5 * 60_000,
  });

  // A request answered with 401 (e.g. expired session) brings the login back.
  useEffect(() => {
    const onUnauthorized = () => void qc.invalidateQueries({ queryKey: authKeys.me });
    window.addEventListener('rhp:unauthorized', onUnauthorized);
    return () => window.removeEventListener('rhp:unauthorized', onUnauthorized);
  }, [qc]);

  if (me.isPending) return <Splash text="Lädt …" />;
  if (me.data) return <AccountContext.Provider value={me.data}>{children}</AccountContext.Provider>;
  if (me.error instanceof ApiError && me.error.status === 401) return <LoginGate />;
  return <Splash text={`Fehler: ${(me.error as Error)?.message ?? 'unbekannt'}`} />;
}

function LoginGate() {
  const providers = useQuery({ queryKey: authKeys.providers, queryFn: () => api.get<Providers>('/auth/providers'), staleTime: Infinity });
  const [sso] = useState(consumeSsoParams);
  const returnTo = window.location.pathname + window.location.search;
  const oidc = providers.data?.oidc ?? null;
  const autoLogin = Boolean(oidc?.autoLogin) && !sso.none && !sso.error && !skipAutoLogin();

  useEffect(() => {
    if (!autoLogin) return;
    markAutoLoginAttempt();
    navigate(ssoLoginUrl(returnTo, true), true);
  }, [autoLogin, returnTo]);

  if (providers.isPending || autoLogin) return <Splash text={oidc ? `Anmeldung mit ${oidc.name} …` : 'Lädt …'} />;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--page)] px-4">
      <div className="w-full max-w-sm text-center">
        <span aria-hidden="true" className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-[var(--accent)] text-[var(--accent-ink)] shadow-sm">
          <svg viewBox="0 0 32 32" className="h-7 w-7" fill="none" stroke="currentColor">
            <path d="M6 15 16 7l10 8v10a1.5 1.5 0 0 1-1.5 1.5h-5V19h-7v7.5h-5A1.5 1.5 0 0 1 6 25z" strokeWidth="2.2" strokeLinejoin="round" />
          </svg>
        </span>
        <h1 className="mt-4 text-2xl font-semibold tracking-tight text-[var(--ink)]">Roths Haus Planer</h1>
        <p className="mt-1 text-sm text-[var(--ink-3)]">Investitionen & Budget – bitte anmelden.</p>
        <div className="mt-8 space-y-4 rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-6 text-left shadow-sm">
          {sso.error ? (
            <p role="alert" className="rounded-lg bg-[var(--critical-soft,rgba(208,59,59,.1))] px-3 py-2 text-sm text-[var(--ink)]">
              {SSO_ERRORS[sso.error] ?? SSO_ERRORS.failed}
            </p>
          ) : null}
          {oidc ? (
            <a
              href={ssoLoginUrl(returnTo)}
              className="flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-[var(--accent)] text-sm font-medium text-[var(--accent-ink)] hover:opacity-90"
            >
              <KeyRound size={16} aria-hidden="true" />
              {oidc.label}
            </a>
          ) : (
            <p className="text-sm text-[var(--ink-2)]">Es ist keine Anmeldung eingerichtet.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function Splash({ text }: { text: string }) {
  return (
    <div role="status" className="flex min-h-screen items-center justify-center bg-[var(--page)] text-sm text-[var(--ink-3)]">
      {text}
    </div>
  );
}

/** Logs out locally and – for SSO sessions – at the identity provider as well. */
export async function logout() {
  setSkipAutoLoginSafe();
  const res = await api.post<{ ok: true; redirect?: string | null }>('/auth/logout');
  navigate(res.redirect ?? '/');
}

function setSkipAutoLoginSafe() {
  try {
    sessionStorage.setItem('rhp-sso-skip', '1');
  } catch {
    /* ignore */
  }
}
